import { Controller } from "@hotwired/stimulus"
import ScriptedVideoChannel from "../../channels/scripted_video_channel"

// Audio tab controller for Scripted Video Studio
// Manages narration generation, music tracks, and sound effects
export default class extends Controller {
  static targets = [
    "voiceSelect", "volumeDisplay",
    "generateButton", "generateIcon", "generateSpinner", "generateLabel",
    "vcButton", "vcButtonProcessing", "vcStatus",
    "enhanceButton", "enhanceSpinner",
    "modelSelect", "audioTagWarning"
  ]
  static values = { configId: String }

  connect() {
    if (this.configIdValue) this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async generateNarration(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    if (!url) return

    this.showGenerating()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const form = button.closest("form")
      const body = form ? new FormData(form) : new FormData()

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`)
      }
      // Job enqueued — spinner stays, ActionCable will deliver result
    } catch (error) {
      this.hideGenerating()
      console.error("[audio] Generate narration failed:", error.message)
    }
  }

  async applyVoiceConsistency(event) {
    event.preventDefault()
    const button = event.currentTarget
    const form = button.closest("form")
    if (!form) return

    const confirmMsg = button.dataset.confirm
    if (confirmMsg && !window.confirm(confirmMsg)) return

    this.showVcProcessing()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = new FormData(form)

      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Request failed (${response.status})`)
      }
      // Job enqueued — spinner stays, ActionCable will deliver result.
      // Fallback: reload after 60s if cable doesn't deliver.
      this.vcTimeout = setTimeout(() => this.turboReload(), 60000)
    } catch (error) {
      this.hideVcProcessing()
      this.showVcStatus("error", error.message || "Failed to start voice processing")
    }
  }

  async generateDialogue(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    if (!url) return

    this.showGenerating()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`)
      }
    } catch (error) {
      this.hideGenerating()
      console.error("[audio] Generate dialogue failed:", error.message)
    }
  }

  async enhanceNarration(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    const textareaId = button.dataset.textareaId
    if (!url || !textareaId) return

    const textarea = document.getElementById(textareaId)
    if (!textarea || !textarea.value.trim()) return

    this.showEnhancing()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ text: textarea.value })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`)
      }

      if (data.enhanced_text) {
        textarea.value = data.enhanced_text
        textarea.dispatchEvent(new Event("input", { bubbles: true }))
        this.checkAudioTagWarning()
      }
    } catch (error) {
      console.error("[audio] Enhance narration failed:", error.message)
    } finally {
      this.hideEnhancing()
    }
  }

  checkAudioTagWarning() {
    if (!this.hasAudioTagWarningTarget || !this.hasModelSelectTarget) return

    const model = this.modelSelectTarget.value
    const isV3 = model === "eleven_v3"
    const textarea = document.getElementById("custom-narration-textarea") ||
                     document.getElementById("scripted-video-narration-text")
    const text = textarea?.value || ""
    const hasTags = /\[(laughs|whispers|sighs|crying|excited|sarcastic|curious|cautiously|cheerfully|mischievously|indecisive|applause|clapping|gunshot|explosion|leaves rustling|gentle footsteps|groaning|exhales|snorts|gulps|swallows|sings|wheezing|pause)\]/i.test(text)

    this.audioTagWarningTarget.classList.toggle("hidden", isV3 || !hasTags)
  }

  // Handle file input changes — submit the parent form when a file is selected
  fileSelected(event) {
    const input = event.target
    if (!input.files || input.files.length === 0) return

    const form = input.closest("form")
    if (form) {
      form.requestSubmit()
    }
  }

  // Update the volume percentage display next to range sliders
  updateVolumeDisplay(event) {
    const slider = event.target
    const percent = Math.round(slider.value * 100)

    const container = slider.closest("div")
    const display = container?.querySelector("[data-scripted-video--audio-target='volumeDisplay']")
    if (display) {
      display.textContent = percent
    }
  }

  // --- Private ---

  subscribeToChannel() {
    this.subscription = ScriptedVideoChannel.subscribe(this.configIdValue, {
      onNarrationUpdate: (data) => this.handleNarrationUpdate(data),
      onVoiceConsistency: (data) => this.handleVoiceConsistency(data)
    })
  }

  handleNarrationUpdate(data) {
    if (data.status === "completed") {
      this.turboReload()
    } else if (data.status === "failed") {
      this.hideGenerating()
      console.error("[audio] Narration generation failed:", data.error)
    }
  }

  showGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.add("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.remove("hidden")
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = "Generating..."
  }

  hideGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = false
      this.generateButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.remove("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.add("hidden")
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = "Generate Narration"
  }

  showEnhancing() {
    if (this.hasEnhanceButtonTarget) {
      this.enhanceButtonTarget.disabled = true
      this.enhanceButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasEnhanceSpinnerTarget) this.enhanceSpinnerTarget.classList.remove("hidden")
  }

  hideEnhancing() {
    if (this.hasEnhanceButtonTarget) {
      this.enhanceButtonTarget.disabled = false
      this.enhanceButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasEnhanceSpinnerTarget) this.enhanceSpinnerTarget.classList.add("hidden")
  }

  handleVoiceConsistency(data) {
    if (this.vcTimeout) clearTimeout(this.vcTimeout)
    this.hideVcProcessing()
    if (data.type === "voice_consistency_complete") {
      if (data.processed > 0) {
        const msg = `Done — ${data.processed} scene(s) processed` +
          (data.skipped > 0 ? `, ${data.skipped} skipped` : "")
        this.showVcStatus("success", msg)
      } else {
        const reason = data.skip_reason || "no eligible scenes"
        this.showVcStatus("error", `No scenes processed — ${reason}`)
      }
    } else {
      this.showVcStatus("error", data.error || "Voice consistency processing failed")
    }
  }

  showVcProcessing() {
    if (this.hasVcButtonTarget) this.vcButtonTarget.classList.add("hidden")
    if (this.hasVcButtonProcessingTarget) this.vcButtonProcessingTarget.classList.remove("hidden")
    this.showVcStatus("processing", "Processing scenes — this may take a few minutes...")
  }

  hideVcProcessing() {
    if (this.hasVcButtonTarget) this.vcButtonTarget.classList.remove("hidden")
    if (this.hasVcButtonProcessingTarget) this.vcButtonProcessingTarget.classList.add("hidden")
  }

  showVcStatus(type, message) {
    if (!this.hasVcStatusTarget) return

    const colors = {
      processing: "bg-blue-50 border-blue-200 text-blue-700",
      success: "bg-green-50 border-green-200 text-green-700",
      error: "bg-red-50 border-red-200 text-red-700"
    }

    const icons = {
      processing: `<svg class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`,
      success: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`,
      error: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
    }

    this.vcStatusTarget.className = `flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${colors[type]}`
    this.vcStatusTarget.innerHTML = `${icons[type]}<span>${message}</span>`
    this.vcStatusTarget.classList.remove("hidden")
  }

  turboReload() {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
